import React, { useEffect, useRef } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { ExpandMore, InfoOutlined } from "@mui/icons-material";
import CustomTooltip from "../CustomTooltip";
import {
  accordianDetailStyles,
  accordianSummary,
  accordianWrapper,
  bottomShadowStyles,
  infoIconStyles,
  marketCellBoxStyle,
  marketPriceCellStyles,
  moreInfoBtnStyles,
  regularCellStyles,
  tableContainerStyles,
  tableHeadCellStyles,
  tableVariantCellStyles,
  topShadowStyles,
  variantCellBoxStyle,
  VATTextStyles,
} from "./styles";
import SupplierProfileModalTooltip from "../CustomTooltips/SupplierProfileModalTooltip.js";
import {
  getAllImageUrlsFromVariants,
  preloadImages,
} from "../../Utils/imageUtils.js";
import { useTranslation } from "react-i18next";
import { formatPrice } from "../../Helpers/formating/index.js";
import useScrollDetection from "./useScrollDetection.js";
import AccordianSummaryContent from "./AccordianSummaryContent.js";

const SingleServiceAccordion = ({
  service,
  viewType,
  isOffered,
  expandedAccordion,
  handleAccordionChange,
  tableConfig,
  currencySymbol,
  isPriclistPage,
  moveToTop,
  showVAtText,
}) => {
  const { t } = useTranslation([
    "single-service-accordion",
    "services",
    "services-description",
    "units",
    "sku",
    "sku-description",
  ]);
  const { description, name, variants } = service;
  const accordionRef = useRef(null);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  // Preload images when accordion is expanded
  useEffect(() => {
    if (expandedAccordion === service.id) {
      const allImageUrls = getAllImageUrlsFromVariants(variants);
      preloadImages(allImageUrls);
    }
  }, [expandedAccordion, service.id, variants]);

  const groupVariants = (variants) => {
    const grouped = {};

    // Filter variants based on isPriclistPage
    const filteredVariants = isPriclistPage
      ? variants
      : variants.filter((variant) => variant.price !== null);

    filteredVariants.forEach((variant) => {
      const sku = variant.sku;

      if (!grouped[sku]) {
        grouped[sku] = {
          sku,
          variants: [],
          more_info: null,
        };
      }

      grouped[sku].variants.push({
        ...variant,
        more_info: {
          ...variant.more_info,
          text: t(variant.more_info.text, { ns: "sku-description" }),
        },
      });
    });

    return Object.values(grouped);
  };

  const groupedVariants = groupVariants(variants);

  const defaultTableConfig = {
    columns: [
      {
        id: "variant",
        label: t("table_columns.sku"),
        render: (variant) => t(variant.sku, { ns: "sku" }),
      },
      {
        id: "unit",
        label: t("table_columns.unit"),
        render: (variant) => t(variant.unit, { ns: "units" }),
        cellStyle: { whiteSpace: "nowrap" },
      },
      {
        id: "basePrice",
        label: t("table_columns.base_price"),
        render: (variant) => {
          const priceValue = formatPrice(variant.price);
          return priceValue ? `${currencySymbol}${priceValue}` : "-";
        },
      },
      {
        id: "discountedPrice",
        label:
          viewType === "invite" || viewType === "cancel"
            ? t("table_columns.price_offered")
            : t("table_columns.after_discount"),
        render: (variant) => {
          const priceValue = formatPrice(variant.after_discount);

          return (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                justifyContent: "space-between",
              }}
            >
              {viewType === "invite" || viewType === "cancel"
                ? "N/A"
                : isOffered && priceValue
                ? `${currencySymbol}${priceValue}`
                : "-"}

              {variant.community_suppliers?.length > 0 ? (
                <SupplierProfileModalTooltip
                  mode="light"
                  communitySuppliers={variant.community_suppliers}
                >
                  <InfoOutlined sx={infoIconStyles} />
                </SupplierProfileModalTooltip>
              ) : (
                ""
              )}
            </Box>
          );
        },
        cellStyle: {
          color: isOffered ? "#24B062 !important" : "#3E3F4CBF",
          fontWeight: "500 !important",
        },
      },
      {
        id: "communityPrice",
        label: t("table_columns.typical_market_price"),
        render: (variant) => {
          const community = variant.in_your_community;

          if (community) {
            const min = formatPrice(community.min);
            const max = formatPrice(community.max);

            if (min != null && max != null) {
              return `${currencySymbol}${min} - ${currencySymbol}${max}`;
            } else if (min != null) {
              return `${currencySymbol}${min}`;
            } else if (max != null) {
              return `${currencySymbol}${max}`;
            }
          }

          return "-";
        },
      },
    ],
  };

  const config = tableConfig || defaultTableConfig;

  const tableContainerRef = useRef(null);
  const { hasHorizontalScroll, hasVerticalScroll } =
    useScrollDetection(tableContainerRef);

  const onOpenMoveToTop = () => {
    accordionRef.current.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };
  return (
    <Accordion
      key={service.id}
      expanded={expandedAccordion === service.id}
      onChange={handleAccordionChange(service.id)}
      sx={accordianWrapper}
      ref={accordionRef}
      TransitionProps={{
        ...(moveToTop ? { onEntered: onOpenMoveToTop } : {}),
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMore />}
        sx={{
          ...accordianSummary(isSmallScreen),
          px: description ? "24px" : "16px",
          py: description ? "16px" : "24px",
        }}
      >
        <AccordianSummaryContent
          description={description}
          name={name}
          isPriclistPage={isPriclistPage}
          t={t}
          pricedVariants={service.priced_variants}
          totalVariants={service.total_variants}
          groupedVariantsLength={groupedVariants.length}
          serviceId={service.id}
        />
      </AccordionSummary>
      <AccordionDetails sx={accordianDetailStyles(isSmallScreen)}>
        <Box
          className="custom-table-container"
          sx={{
            "--border-color": "#EAEAEA",
            "--bg-color": { xs: "#F9F9FC", sm: "#fff" },
            "--corner-radius": { xs: "0px", sm: "8px" },

            position: "relative",
          }}
        >
          {isSmallScreen && (
            <>
              {/* Top shadow */}
              <Box sx={topShadowStyles} />

              {/* Bottom shadow */}
              <Box sx={bottomShadowStyles} />
            </>
          )}
          <TableContainer
            ref={tableContainerRef}
            sx={{
              ...tableContainerStyles(isSmallScreen, hasVerticalScroll),
              overflowY: "auto",
            }}
            className="custom-scrollbar"
          >
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  {config.columns.map((column, index) => (
                    <TableCell
                      key={column.id}
                      sx={tableHeadCellStyles(
                        column,
                        index,
                        config,
                        hasHorizontalScroll
                      )}
                      colSpan={column.colSpan || 1}
                    >
                      {column.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {groupedVariants.map((group, rowIndex) => {
                  const isLastRow = rowIndex === groupedVariants.length - 1;
                  return group.variants.map((variant, index) => {
                    return (
                      <TableRow key={`${variant.id}-${index}`}>
                        {config.columns.map((column) => {
                          if (column.id === "variant" && index === 0) {
                            return (
                              <TableCell
                                key={column.id}
                                rowSpan={group.variants.length}
                                colSpan={column.colSpan || 1}
                                sx={tableVariantCellStyles(
                                  column,
                                  isLastRow,
                                  hasHorizontalScroll
                                )}
                              >
                                <Box sx={variantCellBoxStyle}>
                                  {column.render({ sku: group.sku })}
                                  {variant.more_info &&
                                    ((variant.more_info.text &&
                                      variant.more_info.text.trim() !== "") ||
                                      (variant.more_info.images &&
                                        variant.more_info.images.length >
                                          0)) && (
                                      <CustomTooltip
                                        moreInfo={variant.more_info}
                                      >
                                        <Typography sx={moreInfoBtnStyles}>
                                          {t("more_info")}
                                        </Typography>
                                      </CustomTooltip>
                                    )}
                                </Box>
                              </TableCell>
                            );
                          }

                          if (column.id === "price") {
                            return (
                              <TableCell
                                key={column.id}
                                sx={regularCellStyles(column)}
                              >
                                {column.render(variant, service.id)}
                              </TableCell>
                            );
                          }

                          if (
                            column.id !== "variant" &&
                            column.id !== "market-price" &&
                            column.id !== "communityPrice"
                          ) {
                            return (
                              <TableCell
                                key={column.id}
                                sx={regularCellStyles(column)}
                              >
                                {column.render(variant)}
                              </TableCell>
                            );
                          }

                          if (
                            column.id === "market-price" ||
                            column.id === "communityPrice"
                          ) {
                            return (
                              <TableCell
                                key={column.id}
                                sx={marketPriceCellStyles(isLastRow, column)}
                              >
                                <Box sx={marketCellBoxStyle}>
                                  {column.render(variant)}

                                  {variant.typical_market_price_additional_data ? (
                                    <CustomTooltip
                                      title={
                                        variant.typical_market_price_additional_data
                                      }
                                    >
                                      <Box
                                        sx={{
                                          cursor: "pointer",
                                          display: "flex",
                                        }}
                                      >
                                        <img
                                          src="/icons/info-icon.svg"
                                          alt="info icon"
                                        />
                                      </Box>
                                    </CustomTooltip>
                                  ) : (
                                    <></>
                                  )}
                                </Box>
                              </TableCell>
                            );
                          }

                          return null;
                        })}
                      </TableRow>
                    );
                  });
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {showVAtText ? (
          <Typography sx={VATTextStyles}>
            {t("starting_prices_should_include_vat")}
          </Typography>
        ) : null}
      </AccordionDetails>
    </Accordion>
  );
};

export default SingleServiceAccordion;
